import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/utils/subtitles";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影字幕", to: "/category/movie" },
  { label: "电视剧字幕", to: "/category/tv" },
  { label: "综艺字幕", to: "/category/variety" },
  { label: "动漫字幕", to: "/category/anime" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5" aria-label="中文在线字幕免费高清 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-closed-captioning-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold tracking-tight text-foreground-950">
              中文在线字幕
            </span>
            <span className="mt-0.5 text-[11px] font-medium text-accent-700">免费 · 高清 · 精校</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden md:block">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片或字幕"
                className="w-32 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 xl:w-44"
              />
            </div>
          </form>
          <Link
            to="/about"
            className="hidden whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950 lg:block"
          >
            关于我们
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label="打开菜单"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片或字幕"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-800"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <Link
            to="/about"
            className="mt-2 block rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800"
          >
            关于我们
          </Link>
          <div className="mt-4 flex flex-wrap gap-2">
            {allCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900"
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}